import {
  type DisplayNamesRequest,
  type DisplayNamesResponse,
  type EntityRecordRequest,
  type FieldValueParams,
  type FieldValueResponse,
  type EntityRecordDetail,
  type SearchEntityRecordsResponse,
  type SearchEntityRecordsOptions,
  getDisplayNames,
  getEntityRecord,
  getFieldValues,
  searchEntityRecords,
} from '@kizen/api/records';
import type {
  DefaultError,
  InfiniteQueryObserverOptions,
  QueryObserverOptions,
} from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Drop, Resource, WithParams, WithRequired } from './types';
import { curry1, getResource, parsePageParam } from './utils';

export type SearchEntityRecordsInfiniteQueryOptions = WithParams<
  Omit<SearchEntityRecordsInfiniteQuery, 'queryFn' | 'queryKey'>,
  SearchEntityRecordsOptions['params']
> & {
  body?: SearchEntityRecordsOptions['body'];
  scope?: string[];
};

type DisplayNamesQuery<
  TData = DisplayNamesResponse,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    DisplayNamesResponse,
    TError,
    TData,
    DisplayNamesResponse,
    [Resource<Readonly<['records', 'display-names']>, DisplayNamesRequest>]
  >,
  'queryFn' | 'queryKey'
>;

type EntityRecordQuery<
  TData = EntityRecordDetail,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    EntityRecordDetail,
    TError,
    TData,
    EntityRecordDetail,
    [Resource<Readonly<['records', string, string]>, EntityRecordRequest>]
  >,
  'queryFn' | 'queryKey'
>;

type FieldValueQuery<
  TData = FieldValueResponse,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    FieldValueResponse,
    TError,
    TData,
    FieldValueResponse,
    [
      Resource<
        Readonly<['records', string, 'field-value', string]>,
        FieldValueParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type SearchEntityRecordsInfiniteQuery<
  TData = SearchEntityRecordsResponse,
  TError = DefaultError,
> = WithRequired<
  InfiniteQueryObserverOptions<
    SearchEntityRecordsResponse,
    TError,
    TData,
    SearchEntityRecordsResponse,
    [
      Resource<
        Readonly<['records', string, 'search']>,
        SearchEntityRecordsOptions['params'],
        SearchEntityRecordsOptions['body']
      >,
    ],
    string | number
  >,
  'queryFn' | 'queryKey'
>;

export const displayNamesQuery = <TData = DisplayNamesResponse>(
  axios: AxiosInstance,
  data: DisplayNamesRequest,
  options?: Omit<DisplayNamesQuery<TData>, 'queryFn' | 'queryKey'>
): DisplayNamesQuery<TData> => {
  const path = ['records', 'display-names'] as const;
  const resource = getResource(path, { params: data });

  return <DisplayNamesQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getDisplayNames(axios, qk.params!, { signal });
    },
    ...options,
  };
};

export const entityRecordQuery = <TData = EntityRecordDetail>(
  axios: AxiosInstance,
  objectId: string,
  entityId: string,
  options: WithParams<
    Omit<EntityRecordQuery<TData>, 'queryFn' | 'queryKey'>,
    EntityRecordRequest
  > = {}
): EntityRecordQuery<TData> => {
  const path = ['records', objectId, entityId] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return <EntityRecordQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getEntityRecord(axios, qk.path[1], qk.path[2], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const fieldValuesQuery = <TData = FieldValueResponse>(
  axios: AxiosInstance,
  objectId: string,
  entityId: string,
  options: WithParams<
    Omit<FieldValueQuery<TData>, 'queryFn' | 'queryKey'>,
    FieldValueParams
  > = {}
): FieldValueQuery<TData> => {
  const path = ['records', objectId, 'field-value', entityId] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return <FieldValueQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getFieldValues(axios, qk.path[1], qk.path[3], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const searchEntityRecordsInfiniteQuery = <
  TData = SearchEntityRecordsResponse,
>(
  axios: AxiosInstance,
  objectId: string,
  options: SearchEntityRecordsInfiniteQueryOptions
): SearchEntityRecordsInfiniteQuery<TData> => {
  const path = ['records', objectId, 'search'] as const;
  const { body, params, scope, ...queryOpts } = options;
  const resource = getResource(path, {
    infinite: true,
    method: 'POST',
    body,
    params,
    scope,
  });

  return <SearchEntityRecordsInfiniteQuery<TData>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
        pageParam,
      } = context;

      const page = parsePageParam(pageParam);

      return searchEntityRecords(axios, qk.path[1], {
        body: qk.body,
        params: { ...qk.params, page },
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public displayNamesQuery: <TData = DisplayNamesResponse>(
    ...args: Drop<Parameters<typeof displayNamesQuery<TData>>>
  ) => ReturnType<typeof displayNamesQuery<TData>>;

  public entityRecordQuery: <TData = EntityRecordDetail>(
    ...args: Drop<Parameters<typeof entityRecordQuery<TData>>>
  ) => ReturnType<typeof entityRecordQuery<TData>>;

  public fieldValueQuery: <TData = FieldValueResponse>(
    ...args: Drop<Parameters<typeof fieldValuesQuery<TData>>>
  ) => ReturnType<typeof fieldValuesQuery<TData>>;

  public searchEntityRecordsInfiniteQuery: <
    TData = SearchEntityRecordsResponse,
  >(
    ...args: Drop<Parameters<typeof searchEntityRecordsInfiniteQuery<TData>>>
  ) => ReturnType<typeof searchEntityRecordsInfiniteQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.displayNamesQuery = curry1(displayNamesQuery)(axios);
    this.entityRecordQuery = curry1(entityRecordQuery)(axios);
    this.fieldValueQuery = curry1(fieldValuesQuery)(axios);
    this.searchEntityRecordsInfiniteQuery = curry1(
      searchEntityRecordsInfiniteQuery
    )(axios);
  }
}

export type RecordsQueries = Queries;

export const defRecordsQueries = (axios: AxiosInstance): RecordsQueries => {
  return new Queries(axios);
};
